import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate, periodBadgeBg, periodBadgeColor, periodBadgeText } from '../helpers';
import {
  BG_CARD, BG_PAGE, BORDER, BLACK, TEXT_INK, BRAND_SOFT, BRAND_INK,
  PAUSED_BG, PAUSED_INK, DANGER,
} from '../styles';
import type { Schedule } from '../types';

interface Props {
  schedule: Schedule;
  isLast: boolean;
  isMenuOpen: boolean;
  onToggleMenu: (id: string | number | null) => void;
  onEdit: (schedule: Schedule) => void;
  onOverride: (schedule: Schedule) => void;
  onDuplicate: (id: string | number) => void;
  onSetDefault: (id: string | number) => void;
  onTogglePause: (schedule: Schedule) => void;
  onDelete: (id: string | number) => void;
}

export default function ScheduleRow({
  schedule, isLast, isMenuOpen, onToggleMenu,
  onEdit, onOverride, onDuplicate, onSetDefault, onTogglePause, onDelete,
}: Props) {
  const navigate = useNavigate();

  const menuItem = (text: string, onClick: () => void, color: string = BLACK, border: boolean = true): React.ReactNode => (
    <div
      onClick={onClick}
      style={{
        padding: '10px 14px', fontSize: 13, color,
        cursor: 'pointer',
        borderBottom: border ? '1px solid ' + BORDER : 'none',
      }}
    >
      {text}
    </div>
  );

  return (
    <div style={{
      padding: '18px 20px',
      borderBottom: isLast ? 'none' : '1px solid ' + BORDER,
      display: 'grid',
      gridTemplateColumns: '2fr 1fr 1.5fr 60px',
      gap: 16, alignItems: 'center',
      background: schedule.is_paused ? BG_PAGE : BG_CARD,
      opacity: schedule.is_paused ? 0.6 : 1,
      position: 'relative',
    }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <div style={{
            width: 8, height: 8,
            background: periodBadgeColor(schedule.frequency),
            borderRadius: '50%',
          }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: BLACK }}>{schedule.name}</div>
          {schedule.is_default && (
            <div style={{
              background: BRAND_SOFT, color: BRAND_INK,
              fontSize: 10, padding: '2px 8px', borderRadius: 8, fontWeight: 600,
            }}>DEFAULT</div>
          )}
          {schedule.is_paused && (
            <div style={{
              background: PAUSED_BG, color: PAUSED_INK,
              fontSize: 10, padding: '2px 8px', borderRadius: 8, fontWeight: 600,
            }}>PAUSED</div>
          )}
        </div>
        <div style={{ fontSize: 12, color: TEXT_INK, marginLeft: 16 }}>
          {schedule.periods_per_year} pay periods per year
        </div>
      </div>

      <div>
        <div style={{ fontSize: 13, color: TEXT_INK, marginBottom: 2 }}>Employees</div>
        <div style={{
          fontSize: 16, fontWeight: 600, color: BLACK,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {schedule.employee_count || 0}
        </div>
      </div>

      <div>
        <div style={{ fontSize: 12, color: TEXT_INK, marginBottom: 4 }}>Next pay dates</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {schedule.next_pay_date && (
            <div style={{
              background: periodBadgeBg(schedule.frequency),
              color: periodBadgeText(schedule.frequency),
              padding: '4px 10px', borderRadius: 6,
              fontSize: 11, fontWeight: 600,
            }}>
              {formatDate(schedule.next_pay_date)}
              <button
                onClick={(e) => { e.stopPropagation(); onOverride(schedule); }}
                style={{
                  marginLeft: 10, background: '#FFFFFF', color: '#0F6E56',
                  border: '1.5px solid #15A08C',
                  padding: '4px 10px', borderRadius: 6,
                  fontSize: 11, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >Override</button>
            </div>
          )}
        </div>
      </div>

      <div style={{ textAlign: 'right', position: 'relative' }}>
        <button
          onClick={() => onToggleMenu(isMenuOpen ? null : schedule.id)}
          style={{
            background: 'transparent', border: 'none',
            cursor: 'pointer', fontSize: 18,
            color: TEXT_INK, padding: 4,
          }}
        >⋮</button>
        {isMenuOpen && (
          <div style={{
            position: 'absolute', top: 32, right: 20,
            background: BG_CARD, border: '1px solid ' + BORDER,
            borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            width: 220, overflow: 'hidden', zIndex: 10, textAlign: 'left',
          }}>
            {menuItem('Edit schedule', () => onEdit(schedule))}
            {menuItem('Duplicate', () => onDuplicate(schedule.id))}
            {menuItem(
              'View employees (' + (schedule.employee_count || 0) + ')',
              () => { navigate('/payroll/employees?schedule=' + schedule.id); onToggleMenu(null); },
            )}
            {!schedule.is_default && menuItem('Set as default', () => onSetDefault(schedule.id))}
            {menuItem(
              schedule.is_paused ? 'Resume schedule' : 'Pause schedule',
              () => onTogglePause(schedule),
            )}
            {menuItem('Delete schedule', () => onDelete(schedule.id), DANGER, false)}
          </div>
        )}
      </div>
    </div>
  );
}